import React, { Fragment, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AsyncCallArgs,
  FileUploadButton,
  handleFormSubmission,
  RichTextFormField,
  SubmitFormButton,
  useAsyncCall,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { LawDetails } from "../../../../../types/lawTypes";
import { createLawValidationSchema } from "../../../../../yup/schema/law/lawValidationSchema";
import { Typography } from "@mui/material";
import LawFormFiles from "./LawFormFiles";
import FileDetails from "../../../../components/common/file/FileDetails";
import { lawInitialValues } from "../../../../../init/lawInitialValues";
import { lawSubmission, uploadFileRestricted } from "../../../../../axios/api/services";
import { toast } from "react-toastify";
import useAppDispatch from "../../../../../hooks/redux/base/useAppDispatch";
import LawFormDeleteFileDialog from "./LawFormDeleteFileDialog";
import { VIEW_TYPES } from "../../../../../config/view/types";
import { addViewObject } from "../../../../../store/redux/slice/ViewData/viewDataPage";

const LawForm = ({ law }) => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const dispatch = useAppDispatch();
  const [lawFormRefresher, setLawFormRefresher] = useState(0);

  const reloadLawFormComponent = () => {
    setLawFormRefresher(lawFormRefresher + 1);
  };

  const { methods } = useReactHookForm<LawDetails>({
    defaultValues: { ...law, file: lawInitialValues.file },
    validationSchema: createLawValidationSchema,
  });

  const submit = async (values) => {
    const asyncRegistration: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: lawSubmission(values),
      reactHooksForm: { methods },
      onSuccess: (response) => {
        methods.setValue("description", response.description);
        methods.setValue("files", response.files);
        const viewType = VIEW_TYPES.LAW_EDIT;
        const payload = { viewType, data: response };
        dispatch(addViewObject(payload));
        toast.success(t("m.save.data.success"));
      },
    };
    asyncCall(asyncRegistration);
  };

  const onFileChange = async (e) => {
    const uploadLawFile: AsyncCallArgs = {
      withGlobalBackdrop: true,
      reactHooksForm: { methods },
      promise: uploadFileRestricted(e.target.files[0], "pdf"),
      onSuccess: (response) => {
        const file = { ...response };
        const files = methods.getValues("files");
        files.push({ ...file });
        methods.setValue("files", files);
        methods.setValue("file", lawInitialValues.file);
        methods.clearErrors("file");
      },
      onValidationError: () => {
        methods.setValue("file", lawInitialValues.file);
      },
    };
    asyncCall(uploadLawFile);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmission({ methods, onSubmitFn: submit, t })}>
        <LawFormDeleteFileDialog
          reloadLawFormComponent={reloadLawFormComponent}
          methods={methods}
        ></LawFormDeleteFileDialog>
        <Fragment>
          <RichTextFormField
            fieldName={"description"}
            onChangeMethod={(content) => methods.setValue("description", content)}
          />
        </Fragment>
        <div style={{ marginTop: "20px" }}>
          <LawFormFiles files={methods.getValues("files")}></LawFormFiles>
        </div>
        <div style={{ marginTop: "10px" }}>
          <FileUploadButton accept={"application/pdf"} onFileChange={onFileChange} id="file-upload-modal-file" />
          <FileDetails hint={"m.file.pdf.allowed.type.message"} uploadError={methods.formState.errors?.file as any} />
        </div>
        <Typography align={"right"} mt={5}>
          <SubmitFormButton label={"l.btn.save"} color={"success"} />
        </Typography>
      </form>
    </FormProvider>
  );
};

export default LawForm;
